import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import {
  staggerContainer,
  staggerItem,
  shouldReduceMotion,
} from '@/lib/animations/optimized-animations';
import { GradientBadge } from './GradientBadge';

export interface TrustBadge {
  /** Already-translated badge label. */
  label: string;
  /** Optional leading icon for the badge. */
  icon?: ReactNode;
}

export interface TrustBadgeRowProps {
  /** Trust / compliance badges to render. Labels are pre-translated by the caller. */
  badges: TrustBadge[];
  /** Extra classes merged onto the wrapping row. */
  className?: string;
}

/**
 * TrustBadgeRow — a responsive, wrapping row of trust / compliance badges.
 * Each entry renders as a cool-variant GradientBadge. The row staggers its
 * children in on mount unless reduced motion is preferred, in which case the
 * badges appear statically.
 */
export function TrustBadgeRow({ badges, className }: TrustBadgeRowProps) {
  const reduce = shouldReduceMotion();

  const containerMotion = reduce
    ? {}
    : { variants: staggerContainer, initial: 'initial', animate: 'animate' };

  const itemMotion = reduce ? {} : { variants: staggerItem };

  return (
    <motion.ul
      {...containerMotion}
      className={clsx(
        'flex flex-wrap items-center justify-center gap-3 list-none p-0 m-0',
        className,
      )}
    >
      {badges.map((badge, index) => (
        <motion.li
          key={`${badge.label}-${index}`}
          {...itemMotion}
          className="inline-flex"
        >
          <GradientBadge variant="cool" icon={badge.icon}>
            {badge.label}
          </GradientBadge>
        </motion.li>
      ))}
    </motion.ul>
  );
}
